import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { Link } from 'react-router-dom';
import { landingTokens as T } from '@codex/theme';

// ── Keyframes ─────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: 0% center; }
  100% { background-position: 200% center; }
`;

const cardFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

const orb1 = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  50%       { transform: translate(30px, 20px) scale(1.1); }
`;

const orb2 = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  50%       { transform: translate(-20px, 30px) scale(0.9); }
`;

const orb3 = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  50%       { transform: translate(20px, -20px) scale(1.05); }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.8); }
`;

// ── Typography helpers ────────────────────────────────────────────────────────

const serifFont = `'Cormorant Garant', Georgia, serif`;
const sansFont = `var(--font-body)`;

// ── Shared ────────────────────────────────────────────────────────────────────

/* VlPage is a plain wrapper <div> — the page-level landmarks (header, main,
 * footer) are rendered by the page itself and the section components. */
export const VlPage = styled('div')`
  font-family: ${sansFont};
  background: ${T.bg0};
  color: ${T.text};
  min-height: 100vh;
  overflow-x: hidden;
`;

// ── Buttons ───────────────────────────────────────────────────────────────────

const btnBase = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  border: none;
  cursor: pointer;
  font-family: ${sansFont};
  font-weight: 500;
  border-radius: 8px;
  transition: all 180ms ease;
  white-space: nowrap;
`;

export const BtnPrimary = styled(Link)`
  ${btnBase}
  background: ${T.gold};
  color: ${T.textOnGold};
  font-size: 15px;
  padding: 12px 24px;
  &:hover {
    opacity: 0.88;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(205, 173, 150, 0.3);
  }
`;

export const BtnGhost = styled(Link)`
  ${btnBase}
  background: rgba(255,255,255,0.06);
  color: ${T.text};
  font-size: 15px;
  padding: 12px 24px;
  border: 1px solid ${T.border};
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

export const BtnLg = css`
  padding: 14px 32px !important;
  font-size: 16px !important;
  border-radius: 10px !important;
`;

// ── Nav ───────────────────────────────────────────────────────────────────────

export const LandingNav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  backdrop-filter: blur(20px) saturate(150%);
  background: ${T.overlay};
  border-bottom: 1px solid ${T.border};
`;

export const LandingNavInner = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const LandingNavLogo = styled(Link)`
  display: inline-flex;
  flex-shrink: 0;
`;

export const LandingNavLinks = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  /* Hidden on mobile — hamburger takes over */
  @media (max-width: 900px) {
    display: none;
  }
`;

export const LandingNavLink = styled.a`
  font-size: 14px;
  color: ${T.text2};
  text-decoration: none;
  padding: 6px 14px;
  border-radius: 6px;
  transition: color 150ms;
  white-space: nowrap;
  &:hover {
    color: ${T.text};
  }
`;

export const LandingNavSignIn = styled(Link)`
  font-size: 14px;
  color: ${T.text2};
  text-decoration: none;
  padding: 6px 14px;
  border-radius: 6px;
  transition: color 150ms;
  white-space: nowrap;
  &:hover {
    color: ${T.text};
  }
`;

export const LandingNavCta = styled(Link)`
  font-size: 14px;
  font-weight: 500;
  color: ${T.textOnGold};
  background: ${T.gold};
  text-decoration: none;
  padding: 8px 20px;
  border-radius: 6px;
  margin-left: 8px;
  white-space: nowrap;
  transition:
    opacity 150ms,
    transform 150ms;
  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
  }
`;

// ── Hero ──────────────────────────────────────────────────────────────────────

export const Hero = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding-top: 64px;
  position: relative;
  overflow: hidden;
`;

export const HeroBg = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export const HeroOrb = styled('div')<{ variant: 1 | 2 | 3 }>`
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.35;

  ${({ variant }) =>
    variant === 1 &&
    css`
      width: 600px;
      height: 600px;
      top: -100px;
      left: -100px;
      background: radial-gradient(circle, ${T.gold}, transparent 70%);
      animation: ${orb1} 12s ease-in-out infinite;
    `}
  ${({ variant }) =>
    variant === 2 &&
    css`
      width: 500px;
      height: 500px;
      top: 200px;
      right: -80px;
      background: radial-gradient(circle, ${T.copper}40, transparent 70%);
      animation: ${orb2} 15s ease-in-out infinite;
    `}
  ${({ variant }) =>
    variant === 3 &&
    css`
      width: 400px;
      height: 400px;
      bottom: 0;
      left: 40%;
      background: radial-gradient(circle, ${T.primary}40, transparent 70%);
      animation: ${orb3} 18s ease-in-out infinite;
    `}
`;

export const HeroGrain = styled('div')`
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
  opacity: 0.4;
`;

export const HeroInner = styled('div')`
  position: relative;
  z-index: 2;
  max-width: 1600px;
  margin: 0 auto;
  padding: 80px 24px;
  width: 75%;
  min-width: 340px;
  transition: width 300ms ease;

  @media (max-width: 1200px) {
    width: 90%;
  }

  @media (max-width: 1024px) {
    width: 55%;
  }
`;

export const HeroEyebrow = styled('p')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${T.gold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 28px;
  padding: 6px 14px;
  background: ${T.gold}1a;
  border: 1px solid ${T.gold}33;
  border-radius: 100px;
`;

export const EyebrowDot = styled('span')`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${T.gold};
  animation: ${pulseDot} 2s ease-in-out infinite;
`;

export const HeroHeadline = styled('h1')`
  font-family: ${serifFont};
  font-size: clamp(52px, 7vw, 70px);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: ${T.cream};
  margin: 0 0 24px;
`;

export const HeadlineShimmer = styled('span')`
  background: linear-gradient(135deg, ${T.gold} 0%, ${T.copper} 40%, ${T.gold} 80%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 4s linear infinite;
`;

export const HeroSub = styled('p')`
  font-size: 18px;
  font-weight: 300;
  line-height: 1.7;
  color: ${T.text2};
  max-width: 520px;
  margin: 0 0 40px;
`;

export const HeroActions = styled('div')`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

// ── Avatar stack ──────────────────────────────────────────────────────────────

export const AvatarStack = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const AvatarBubble = styled('div')<{ index: number }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${T.goldDim}, ${T.copper});
  border: 2px solid ${T.bg0};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: ${T.textOnGold};
  margin-left: ${({ index }) => (index === 0 ? 0 : -8)}px;
  z-index: ${({ index }) => 5 - index};
`;

export const AvatarCount = styled('span')`
  font-size: 13px;
  color: ${T.text2};
  margin-left: 4px;
`;

// ── Hero visual card ──────────────────────────────────────────────────────────

export const HeroVisual = styled('div')`
  position: absolute;
  right: max(24px, calc(50% - 560px));
  top: 50%;
  transform: translateY(-50%);
  width: 400px;
  z-index: 2;

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const HeroCard = styled('div')`
  position: relative;
  width: 360px;
  background: ${T.bg2};
  border: 1px solid ${T.border};
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.04);
  animation: ${cardFloat} 6s ease-in-out infinite;
`;

export const HeroCardHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px 12px;
  border-bottom: 1px solid ${T.border};
`;

export const CardDots = styled('div')`
  display: flex;
  gap: 5px;
  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${T.text3};
  }
  span:nth-of-type(1) {
    background: #ff5f56;
  }
  span:nth-of-type(2) {
    background: #ffbd2e;
  }
  span:nth-of-type(3) {
    background: #27c93f;
  }
`;

export const CardTitle = styled('span')`
  font-family: ${serifFont};
  font-size: 13px;
  color: ${T.text2};
  flex: 1;
  text-align: center;
`;

export const HeroCardContent = styled('div')`
  padding: 20px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CardMeta = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const CardAvatar = styled('div')`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${T.goldDim}, ${T.copper});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: ${T.textOnGold};
  flex-shrink: 0;
`;

export const CardAuthorName = styled('span')`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${T.text};
`;

export const CardDate = styled('time')`
  font-size: 11px;
  color: ${T.text3};
  margin-top: 2px;
`;

export const CardBadge = styled('div')`
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  color: ${T.gold};
  background: ${T.gold}1f;
  border: 1px solid ${T.gold}33;
  padding: 2px 8px;
  border-radius: 100px;
  flex-shrink: 0;
`;

export const CardLines = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CardLine = styled('div')<{ width: string }>`
  height: 8px;
  width: ${({ width }) => width};
  background: ${T.bg3};
  border-radius: 4px;
`;

export const CardStats = styled('div')`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: ${T.text3};
  border-top: 1px solid ${T.border};
  padding-top: 14px;
`;

// ── Floating pills ────────────────────────────────────────────────────────────

const FloatBase = styled('div')`
  position: absolute;
  background: ${T.bg2};
  border: 1px solid ${T.border};
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
`;

export const FloatEarnings = styled(FloatBase)`
  right: -16px;
  bottom: 20px;
  animation: ${cardFloat} 7s ease-in-out infinite reverse;
`;

export const FloatSubs = styled(FloatBase)`
  left: -40px;
  top: 30px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${cardFloat} 8s 1s ease-in-out infinite;
`;

export const FloatLabel = styled('p')`
  font-size: 11px;
  color: ${T.text3};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
`;

export const FloatValue = styled('p')`
  font-family: ${serifFont};
  font-size: 22px;
  font-weight: 600;
  color: ${T.cream};
`;

export const FloatTrend = styled('span')`
  align-items: center;
  color: #4ade80;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
`;

// ── Stats bar ─────────────────────────────────────────────────────────────────

export const StatsBar = styled.section`
  background: ${T.bg1};
  border-top: 1px solid ${T.border};
  border-bottom: 1px solid ${T.border};
  padding: 40px 0;
`;

export const StatsInner = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StatItem = styled('div')`
  text-align: center;
  padding: 0 24px;
  border-right: 1px solid ${T.border};
  &:last-child {
    border-right: none;
  }
  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid ${T.border};
    padding: 16px 0;
    &:nth-of-type(even) {
      border-bottom: none;
    }
  }
`;

export const StatValue = styled('div')`
  font-family: ${serifFont};
  font-size: 36px;
  font-weight: 600;
  color: ${T.cream};
  line-height: 1;
  margin-bottom: 8px;
`;

export const StatPrefix = styled('span')`
  font-size: 24px;
  color: ${T.gold};
  margin-right: 2px;
`;

export const StatLabel = styled('div')`
  font-size: 13px;
  color: ${T.text3};
  letter-spacing: 0.02em;
`;

// ── Section container ─────────────────────────────────────────────────────────

export const Section = styled('section', {
  shouldForwardProp: (prop) => prop !== 'alt' && prop !== 'dark',
})<{ alt?: boolean; dark?: boolean }>`
  padding: 100px 0;
  background: ${({ alt, dark }) => (alt ? T.bg1 : dark ? T.bg2 : T.bg0)};
`;

export const Container = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

export const SectionHeader = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 64px;
`;

export const SectionEyebrow = styled('div')`
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  color: ${T.gold};
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

export const SectionTitle = styled('h2')<{ left?: boolean }>`
  font-family: ${serifFont};
  font-size: clamp(36px, 4vw, 48px);
  font-weight: 600;
  line-height: 1.1;
  color: ${T.cream};
  letter-spacing: -0.02em;
  margin: 0 0 20px;
  ${({ left }) => left && 'text-align: left;'}
  em {
    font-style: italic;
  }
`;

export const SectionSub = styled('p')<{ left?: boolean }>`
  font-size: 17px;
  font-weight: 300;
  line-height: 1.7;
  color: ${T.text2};
  margin: 0;
  ${({ left }) => left && 'text-align: left;'}
`;

// ── Features ──────────────────────────────────────────────────────────────────

export const FeaturesGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 80px;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureCard = styled('div')<{ accent?: boolean }>`
  padding: 36px 32px;
  background: ${({ accent }) =>
    accent ? `linear-gradient(135deg, ${T.gold}14, ${T.copper}0f)` : T.bg1};
  transition: background 200ms;
  &:hover {
    background: ${({ accent }) =>
      accent ? `linear-gradient(135deg, ${T.gold}24, ${T.copper}1a)` : T.bg2};
  }
`;

export const FeatureIcon = styled('span')`
  font-size: 24px;
  color: ${T.gold};
  margin-bottom: 16px;
  display: block;
`;

export const FeatureTitle = styled('h3')`
  font-family: ${serifFont};
  font-size: 22px;
  font-weight: 600;
  color: ${T.cream};
  margin: 0 0 10px;
`;

export const FeatureBody = styled('p')`
  font-size: 15px;
  font-weight: 300;
  line-height: 1.7;
  color: ${T.text2};
  margin: 0;
`;

// ── Spotlight ─────────────────────────────────────────────────────────────────

export const Spotlight = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  padding: 64px;
  background: ${T.bg1};
  border-radius: 20px;
  border: 1px solid ${T.border};
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const SpotlightTitle = styled('h3')`
  font-family: ${serifFont};
  font-size: 34px;
  font-weight: 600;
  color: ${T.cream};
  line-height: 1.2;
  margin: 0 0 20px;
`;

export const SpotlightBody = styled('p')`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.8;
  color: ${T.text2};
  margin: 0 0 28px;
`;

export const SpotlightList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 36px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  li {
    font-size: 15px;
    color: ${T.text};
  }
`;

// ── Tier preview (in spotlight) ───────────────────────────────────────────────

export const TiersPreview = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 14px;
  overflow: hidden;
`;

export const TierPreviewRow = styled('div')`
  background: ${T.bg2};
  padding: 20px 24px;
  display: grid;
  grid-template-columns: 16px 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 14px;
  row-gap: 6px;
  align-items: center;
  transition: background 150ms;
  &:hover {
    background: ${T.bg3};
  }
`;

export const TierDot = styled('div')<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
  grid-row: 1;
`;

export const TierName = styled('div')`
  grid-column: 2;
  grid-row: 1;
  font-size: 15px;
  font-weight: 500;
  color: ${T.cream};
`;

export const TierPrice = styled('div')`
  grid-column: 3;
  grid-row: 1;
  font-family: ${serifFont};
  font-size: 16px;
  color: ${T.gold};
  font-weight: 600;
`;

export const TierPerks = styled('div')`
  grid-column: 2 / 4;
  grid-row: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  span {
    font-size: 11px;
    color: ${T.text3};
    background: ${T.bg3};
    padding: 2px 8px;
    border-radius: 100px;
  }
`;

// ── Reader split ──────────────────────────────────────────────────────────────

export const ReaderSplit = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const ReaderVisual = styled('div')`
  position: relative;
  display: flex;
  padding-bottom: 40px;
  padding-right: 40px;
`;

export const ReaderFloat = styled('div')`
  position: absolute;
  bottom: 0;
  right: 0;
  background: ${T.bg2};
  border: 1px solid ${T.border};
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);

  @media (max-width: 480px) {
    position: static;
    margin-top: 16px;
    align-self: flex-end;
  }
`;

export const ReaderFeatureList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 36px;
`;

export const ReaderFeatureItem = styled('div')`
  display: flex;
  gap: 16px;
  .icon {
    font-size: 18px;
    color: ${T.gold};
    flex-shrink: 0;
    margin-top: 2px;
  }
  strong {
    display: block;
    font-size: 15px;
    font-weight: 500;
    color: ${T.cream};
    margin-bottom: 4px;
  }
  p {
    font-size: 14px;
    font-weight: 300;
    line-height: 1.6;
    color: ${T.text2};
    margin: 0;
  }
`;

// ── Placeholder ───────────────────────────────────────────────────────────────

export const Placeholder = styled('div')<{ height?: number | string }>`
  width: 100%;
  height: ${({ height }) => (typeof height === 'number' ? `${height}px` : (height ?? '300px'))};
  background: linear-gradient(135deg, ${T.bg2}, ${T.bg3});
  border-radius: 20px;
  border: 1px solid ${T.border};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, ${T.gold}0a 0%, transparent 50%, ${T.copper}0a 100%);
  }
`;

export const PlaceholderLabel = styled('span')`
  font-size: 12px;
  color: ${T.text3};
  text-align: center;
  padding: 16px;
  font-style: italic;
  position: relative;
  z-index: 1;
`;

// ── Testimonials ──────────────────────────────────────────────────────────────

export const TestimonialsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const TestimonialCard = styled.figure`
  background: ${T.bg1};
  border: 1px solid ${T.border};
  border-radius: 20px;
  padding: 36px;
  margin: 0;
  position: relative;
  transition:
    border-color 200ms,
    transform 200ms;
  &:hover {
    border-color: ${T.gold}4d;
    transform: translateY(-4px);
  }
  &::before {
    content: '"';
    position: absolute;
    top: 20px;
    right: 28px;
    font-family: ${serifFont};
    font-size: 80px;
    line-height: 1;
    color: ${T.gold};
    opacity: 0.15;
  }
`;

export const TestimonialQuote = styled.blockquote`
  font-family: ${serifFont};
  font-size: 18px;
  font-weight: 400;
  font-style: italic;
  line-height: 1.65;
  color: ${T.text};
  margin: 0 0 28px;
`;

export const TestimonialAuthor = styled.figcaption`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const TestimonialAvatar = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${T.goldDim}, ${T.copper});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${T.textOnGold};
  flex-shrink: 0;
`;

export const TestimonialName = styled.strong`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${T.cream};
`;

export const TestimonialRole = styled('span')`
  display: block;
  font-size: 12px;
  color: ${T.text3};
  margin-top: 2px;
`;

// ── Steps ─────────────────────────────────────────────────────────────────────

export const StepsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

export const StepItem = styled('div')`
  position: relative;
`;

export const StepNumber = styled('div')`
  font-family: ${serifFont};
  font-size: 64px;
  font-weight: 600;
  color: ${T.gold}26;
  line-height: 1;
  margin-bottom: 16px;
`;

export const StepConnector = styled('div')`
  position: absolute;
  top: 32px;
  right: -20px;
  width: 40px;
  height: 1px;
  background: linear-gradient(90deg, ${T.goldDim}, transparent);
  @media (max-width: 1024px) {
    display: none;
  }
`;

export const StepTitle = styled('h3')`
  font-family: ${serifFont};
  font-size: 24px;
  font-weight: 600;
  color: ${T.cream};
  margin: 0 0 12px;
`;

export const StepBody = styled('p')`
  font-size: 15px;
  font-weight: 300;
  line-height: 1.7;
  color: ${T.text2};
  margin: 0;
`;

// ── Pricing tiers ─────────────────────────────────────────────────────────────

export const TiersGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 32px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const TierCard = styled('div')<{ featured?: boolean }>`
  padding: 40px 36px;
  position: relative;
  background: ${T.bg1};
  transition: background 200ms;
  &:hover {
    background: ${T.bg2};
  }
  ${({ featured }) =>
    featured &&
    css`
      background: linear-gradient(160deg, ${T.gold}1a, ${T.copper}12);
      border: 1px solid ${T.gold}33;
      border-radius: 20px;
      margin: -2px;
      z-index: 1;
      &:hover {
        background: linear-gradient(160deg, ${T.gold}29, ${T.copper}1f);
      }
    `}
`;

export const TierBadge = styled('div')`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: ${T.textOnGold};
  background: ${T.gold};
  padding: 3px 10px;
  border-radius: 100px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 20px;
`;

export const TierNameLabel = styled('div')`
  font-size: 13px;
  font-weight: 500;
  color: ${T.text3};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

export const TierPriceWrap = styled('div')`
  margin-bottom: 32px;
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

export const TierPriceCurrency = styled('span')`
  font-size: 20px;
  font-weight: 500;
  color: ${T.gold};
  align-self: flex-start;
  margin-top: 8px;
`;

export const TierPriceAmount = styled('span')`
  font-family: ${serifFont};
  font-size: 48px;
  font-weight: 600;
  color: ${T.cream};
  line-height: 1;
`;

export const TierPricePeriod = styled('span')`
  font-size: 14px;
  color: ${T.text3};
`;

export const TierPerksList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 36px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const TierPerkItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${T.text2};
  .check {
    font-size: 10px;
    color: ${T.gold};
    flex-shrink: 0;
  }
`;

export const TierCta = styled(Link)<{ featured?: boolean }>`
  display: block;
  text-align: center;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  border: 1px solid ${T.border};
  color: ${T.text};
  background: rgba(255, 255, 255, 0.04);
  transition: all 150ms;
  &:hover {
    background: rgba(255, 255, 255, 0.09);
    transform: translateY(-1px);
  }
  ${({ featured }) =>
    featured &&
    css`
      background: ${T.gold};
      color: ${T.textOnGold};
      border-color: transparent;
      &:hover {
        opacity: 0.88;
        background: ${T.gold};
      }
    `}
`;

export const PricingNote = styled('p')`
  text-align: center;
  font-size: 13px;
  color: ${T.text3};
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
`;

// ── Comparison table ──────────────────────────────────────────────────────────

export const ComparisonWrap = styled('div')`
  max-width: 720px;
  margin: 0 auto;
`;

export const ComparisonTable = styled('div')`
  background: ${T.bg1};
  border-radius: 20px;
  border: 1px solid ${T.border};
  overflow: hidden;
`;

export const ComparisonHeader = styled('div')`
  display: grid;
  grid-template-columns: 1fr 120px 120px;
  padding: 16px 24px;
  background: ${T.bg2};
  border-bottom: 1px solid ${T.border};
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const ComparisonColLabel = styled('div')<{ us?: boolean }>`
  text-align: center;
  font-weight: 600;
  color: ${({ us }) => (us ? T.gold : T.text3)};
`;

export const ComparisonRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 120px 120px;
  padding: 14px 24px;
  border-bottom: 1px solid ${T.border};
  transition: background 100ms;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${T.bg2};
  }
`;

export const ComparisonFeature = styled('div')`
  font-size: 14px;
  color: ${T.text2};
  display: flex;
  align-items: center;
`;

export const ComparisonCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CheckIcon = styled('span')<{ variant: 'yes' | 'no' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: ${({ variant }) => (variant === 'yes' ? T.gold : variant === 'no' ? T.text3 : T.text2)};
  opacity: ${({ variant }) => (variant === 'no' ? 0.5 : 1)};
  & > svg {
    width: 16px;
    height: 16px;
  }
`;

// ── Final CTA ─────────────────────────────────────────────────────────────────

export const CtaSection = styled.section`
  position: relative;
  text-align: center;
  padding: 120px 24px;
  overflow: hidden;
`;

export const CtaBg = styled('div')`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export const CtaOrb = styled('div')`
  position: absolute;
  width: 800px;
  height: 800px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, ${T.gold}1f, transparent 65%);
  border-radius: 50%;
`;

export const CtaInner = styled('div')`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

export const CtaTitle = styled('h2')`
  font-family: ${serifFont};
  font-size: clamp(40px, 5vw, 64px);
  font-weight: 600;
  line-height: 1.1;
  color: ${T.cream};
  letter-spacing: -0.02em;
  margin: 16px 0 24px;
`;

export const CtaSub = styled('p')`
  font-size: 17px;
  font-weight: 300;
  line-height: 1.7;
  color: ${T.text2};
  margin: 0 0 40px;
`;

// ── Footer ────────────────────────────────────────────────────────────────────

export const Footer = styled.footer`
  background: ${T.bg1};
  border-top: 1px solid ${T.border};
  padding: 64px 24px 32px;
`;

export const FooterInner = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 64px;
  margin-bottom: 48px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const FooterBrand = styled('div')``;

export const FooterTagline = styled('p')`
  font-size: 14px;
  color: ${T.text3};
  margin: 12px 0 0;
  font-weight: 300;
  line-height: 1.6;
`;

export const FooterLinks = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const FooterCol = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  strong {
    font-size: 12px;
    font-weight: 600;
    color: ${T.text2};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  a {
    font-size: 14px;
    color: ${T.text3};
    text-decoration: none;
    transition: color 150ms;
    &:hover {
      color: ${T.text};
    }
  }
`;

export const FooterBottom = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid ${T.border};
  max-width: 1200px;
  padding-top: 24px;
  margin: 0 auto;
  gap: 12px 16px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

export const FooterBottomInner = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  max-width: 100%;
  font-size: 12px;
  color: ${T.text3};
  flex: 1 1 auto;
  min-width: 0;
  a {
    color: ${T.text3};
    text-decoration: none;
    &:hover {
      color: ${T.text2};
    }
  }

  @media (max-width: 768px) {
    flex: 0 0 auto;
    justify-content: center;
    text-align: center;
  }
`;

/* Wrapper for the live status badge. On wide viewports it sits to the
 * right of the legal bar inside <FooterBottom>; below 768px the row
 * collapses and the badge takes its own line, centered. The badge itself
 * is rendered by <StatusBadge>. */
export const FooterStatus = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

export const FooterSep = styled('span')`
  opacity: 0.4;
`;
